import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useAppData } from '~/composables/useAppData'
import { Normalization } from '~/utils/normalization'

interface FilterCategory {
  id: string | number
  slug?: string
  name?: string
}
type FilterId = string | number
interface FilterCookie {
  selectedCategory: FilterCategory | null
  selectedDietaryOptions: FilterId[]
  selectedRatings: FilterId[]
  selectedOtherFilters: string[]
  selectedReliability: boolean
  selectedDistance: FilterId | null
}
interface FilterUrlInput {
  category?: string
  dietaryOptions?: string
  ratings?: string
  groupOrdering?: string
  individualPackaging?: string
  isNew?: string
  smallBusiness?: string
  reliabilitySuperstar?: string
  distance?: string
  tags?: string
}

const toFilterId = (value: unknown): FilterId | null =>
  typeof value === 'string' || typeof value === 'number' ? value : null

const toFilterIdList = (value: unknown): FilterId[] =>
  Normalization.arrayValue<unknown>(value).filter(
    (item): item is FilterId => typeof item === 'string' || typeof item === 'number'
  )

const toFilterCategory = (value: unknown): FilterCategory | null => {
  if (value === null || typeof value !== 'object') return null
  const candidate = value as Record<string, unknown>
  const id = toFilterId(candidate['id'])
  if (id === null) return null

  const category: FilterCategory = { id }
  const slug = Normalization.stringValue(candidate['slug'])
  if (slug !== null && slug.length > 0) category.slug = slug
  const name = Normalization.stringValue(candidate['name'])
  if (name !== null && name.length > 0) category.name = name
  return category
}

export const useFiltersStore = defineStore('filters', () => {
  // Filter states
  const selectedCategory = ref<FilterCategory | null>(null)
  const selectedDietaryOptions = ref<FilterId[]>([])
  const selectedRatings = ref<FilterId[]>([])
  const selectedOtherFilters = ref<string[]>([])
  const selectedReliability = ref(false)
  const selectedDistance = ref<FilterId | null>(null)
  const selectedTags = ref<number[]>([])

  // Get dietary options from global useAppData instead of maintaining our own copy
  const { dietaryOptions } = useAppData()

  // Cookie persistence using Nuxt 4's useCookie
  const filtersCookie = useCookie<FilterCookie>('mealz-filters', {
    default: () => ({
      selectedCategory: null,
      selectedDietaryOptions: [],
      selectedRatings: [],
      selectedOtherFilters: [],
      selectedReliability: false,
      selectedDistance: null
    }),
    // Cookie options
    maxAge: 60 * 60 * 24 * 7, // 7 days
    path: '/',
    sameSite: 'lax'
  })

  // Restore filters from cookie (SSR-safe)
  const restoreFiltersFromCookie = () => {
    const saved = filtersCookie.value
    if (saved.selectedCategory !== null) {
      selectedCategory.value = toFilterCategory(saved.selectedCategory)
    }
    if (Array.isArray(saved.selectedDietaryOptions)) {
      selectedDietaryOptions.value = toFilterIdList(saved.selectedDietaryOptions)
    }
    if (Array.isArray(saved.selectedRatings)) {
      selectedRatings.value = toFilterIdList(saved.selectedRatings)
    }
    if (Array.isArray(saved.selectedOtherFilters)) {
      selectedOtherFilters.value = Normalization.stringList(saved.selectedOtherFilters)
    }
    if (saved.selectedReliability !== undefined) {
      selectedReliability.value = Normalization.boolValue(saved.selectedReliability)
    }
    if (saved.selectedDistance !== undefined) {
      selectedDistance.value = toFilterId(saved.selectedDistance)
    }
  }

  // Save filters to cookie
  const saveFiltersToCookie = () => {
    filtersCookie.value = {
      selectedCategory: selectedCategory.value,
      selectedDietaryOptions: selectedDietaryOptions.value,
      selectedRatings: selectedRatings.value,
      selectedOtherFilters: selectedOtherFilters.value,
      selectedReliability: selectedReliability.value,
      selectedDistance: selectedDistance.value
    }
  }

  // Auto-save filters whenever they change
  watch(
    [
      selectedCategory,
      selectedDietaryOptions,
      selectedRatings,
      selectedOtherFilters,
      selectedReliability,
      selectedDistance
    ],
    () => {
      saveFiltersToCookie()
    },
    { deep: true }
  )

  // Restore filters on store initialization
  restoreFiltersFromCookie()

  // Computed properties
  const hasActiveFilters = computed(() => {
    return (
      selectedCategory.value !== null ||
      selectedDietaryOptions.value.length > 0 ||
      selectedRatings.value.length > 0 ||
      selectedOtherFilters.value.length > 0 ||
      selectedReliability.value ||
      selectedDistance.value !== null ||
      selectedTags.value.length > 0
    )
  })

  const activeFiltersCount = computed(() => {
    let count = 0
    if (selectedCategory.value) count++
    if (selectedDietaryOptions.value.length > 0) count++
    if (selectedRatings.value.length > 0) count++
    if (selectedOtherFilters.value.length > 0) count++
    if (selectedReliability.value) count++
    if (selectedDistance.value !== null) count++
    if (selectedTags.value.length > 0) count++
    return count
  })

  // Category selection
  const setSelectedCategory = (category: FilterCategory | null) => {
    // Use slug for comparison if available, otherwise fallback to id
    const currentSlug = selectedCategory.value?.slug ?? selectedCategory.value?.id
    const newSlug = category?.slug ?? category?.id
    selectedCategory.value = currentSlug === newSlug ? null : category
  }

  const clearSelectedCategory = () => {
    selectedCategory.value = null
  }

  // Ratings
  const setRating = (rating: FilterId) => {
    const index = selectedRatings.value.indexOf(rating)
    if (index > -1) {
      selectedRatings.value.splice(index, 1)
    } else {
      selectedRatings.value = [rating] // Single selection
    }
  }

  const clearRatings = () => {
    selectedRatings.value = []
  }

  // Other filters
  const toggleOtherFilter = (filterId: string) => {
    const index = selectedOtherFilters.value.indexOf(filterId)
    if (index > -1) {
      selectedOtherFilters.value.splice(index, 1)
    } else {
      selectedOtherFilters.value.push(filterId)
    }
  }

  const clearOtherFilters = () => {
    selectedOtherFilters.value = []
  }

  // Reliability filter
  const toggleReliability = () => {
    selectedReliability.value = !selectedReliability.value
  }

  const clearReliability = () => {
    selectedReliability.value = false
  }

  // Dietary options
  const toggleDietaryOption = (optionId: FilterId) => {
    // optionId is the slug, we need to find the API ID
    const option = dietaryOptions.value.find((opt) => opt.slug === optionId)
    const apiId = option ? option.id : optionId

    const index = selectedDietaryOptions.value.indexOf(apiId)
    if (index > -1) {
      selectedDietaryOptions.value.splice(index, 1)
    } else {
      selectedDietaryOptions.value.push(apiId)
    }
  }

  const clearDietaryOptions = () => {
    selectedDietaryOptions.value = []
  }

  // Get dietary options for modal format
  const getDietaryOptionsForModal = (): {
    id: string | number
    label: string
    isActive: boolean
  }[] => {
    return dietaryOptions.value.map((option) => ({
      id: option.slug, // Use slug for display/UI
      label: option.label,
      isActive: selectedDietaryOptions.value.includes(option.id) // option.id is the API ID
    }))
  }

  // Clear all filters
  const clearAllFilters = () => {
    selectedCategory.value = null
    selectedDietaryOptions.value = []
    selectedRatings.value = []
    selectedOtherFilters.value = []
    selectedReliability.value = false
    selectedDistance.value = null
    selectedTags.value = []
  }

  // Notify that filters have been restored from URL
  const notifyFiltersRestored = () => {
    selectedDietaryOptions.value = [...selectedDietaryOptions.value]
    selectedRatings.value = [...selectedRatings.value]
    selectedOtherFilters.value = [...selectedOtherFilters.value]
    selectedTags.value = [...selectedTags.value]
  }

  // Smart restoration: URL first, then cookies, then defaults
  const smartRestoreFilters = (urlFilters: FilterUrlInput = {}) => {
    // Priority 1: URL parameters (most up-to-date)
    const categoryInput = Normalization.trimmedStringValue(urlFilters.category)
    if (categoryInput !== null) {
      // Find category by slug or ID - use categories from useAppData, not dietaryOptions
      const { categories } = useAppData()
      const category = categories.value.find(
        (cat) => cat.slug === categoryInput || cat.id === categoryInput
      )
      if (category) selectedCategory.value = category
    }

    const dietaryOptionsInput = Normalization.trimmedStringValue(urlFilters.dietaryOptions)
    if (dietaryOptionsInput !== null) {
      // Convert slugs from URL to API IDs for storage
      const dietaryIds = dietaryOptionsInput.split(',').map((slug) => {
        // Find by slug (since that's what's in the URL)
        const option = dietaryOptions.value.find((opt) => opt.slug === slug)
        return option ? option.id : slug
      })
      selectedDietaryOptions.value = dietaryIds
    }

    const ratingsInput = Normalization.trimmedStringValue(urlFilters.ratings)
    if (ratingsInput !== null) {
      selectedRatings.value = Normalization.trimmedStringList(ratingsInput)
    }

    // Handle individual other filter parameters
    const otherFilters = []
    if (Normalization.boolValue(urlFilters.groupOrdering)) {
      otherFilters.push('groupOrdering')
    }
    if (Normalization.boolValue(urlFilters.individualPackaging)) {
      otherFilters.push('individualPackaging')
    }
    if (Normalization.boolValue(urlFilters.isNew)) {
      otherFilters.push('new')
    }
    if (Normalization.boolValue(urlFilters.smallBusiness)) {
      otherFilters.push('smallBusiness')
    }
    selectedOtherFilters.value = otherFilters

    if (urlFilters.reliabilitySuperstar !== undefined) {
      selectedReliability.value = Normalization.boolValue(urlFilters.reliabilitySuperstar)
    }

    const distanceInput = Normalization.trimmedStringValue(urlFilters.distance)
    if (distanceInput !== null) {
      selectedDistance.value = distanceInput
    }

    const tagsInput = Normalization.trimmedStringValue(urlFilters.tags)
    if (tagsInput !== null) {
      selectedTags.value = Normalization.intList(tagsInput)
    }

    // Priority 2: If no URL filters, restore from cookies
    if (!Normalization.satisfiesMinProperties(urlFilters, 1)) {
      restoreFiltersFromCookie()
    }
  }

  // Get all active filters for search
  const getActiveFilters = (): Record<string, FilterId | FilterId[] | boolean> => {
    const filters: Record<string, FilterId | FilterId[] | boolean> = {}

    if (selectedCategory.value) {
      // Use ID for API requests
      filters['category_ids'] = selectedCategory.value.id
    }

    if (selectedDietaryOptions.value.length > 0) {
      // selectedDietaryOptions now contains API IDs, so we can use them directly
      filters['dietaryOptions'] = selectedDietaryOptions.value
    }

    if (selectedRatings.value.length > 0) {
      filters['ratings'] = selectedRatings.value
    }

    // Send each other filter as a separate parameter
    if (selectedOtherFilters.value.includes('groupOrdering')) {
      filters['groupOrdering'] = true
    }

    if (selectedOtherFilters.value.includes('individualPackaging')) {
      filters['individualPackaging'] = true
    }

    if (selectedOtherFilters.value.includes('new')) {
      filters['isNew'] = true
    }

    if (selectedOtherFilters.value.includes('smallBusiness')) {
      filters['smallBusiness'] = true
    }

    if (selectedReliability.value) {
      filters['reliabilitySuperstar'] = true
    }

    if (selectedDistance.value !== null) {
      filters['distance'] = selectedDistance.value
    }

    if (selectedTags.value.length > 0) {
      filters['tags'] = selectedTags.value
    }

    return filters
  }

  return {
    // State
    selectedCategory,
    selectedDietaryOptions,
    selectedRatings,
    selectedOtherFilters,
    selectedReliability,
    selectedDistance,
    selectedTags,
    dietaryOptions,

    // Computed
    hasActiveFilters,
    activeFiltersCount,

    // Methods
    setSelectedCategory,
    clearSelectedCategory,
    toggleDietaryOption,
    clearDietaryOptions,
    setRating,
    clearRatings,
    toggleOtherFilter,
    clearOtherFilters,
    toggleReliability,
    clearReliability,
    getDietaryOptionsForModal,
    clearAllFilters,
    notifyFiltersRestored,
    smartRestoreFilters,
    restoreFiltersFromCookie,
    getActiveFilters
  }
})
